import { useEffect, useState } from "react";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaEnvelope,
  FaPassport,
  FaPhoneAlt,
  FaWhatsapp,
} from "react-icons/fa";
import { Link, Navigate, useParams } from "react-router-dom";
import { indiaSupport } from "../content/indiaSiteContent";
import { cmsApi } from "../services/cms";
import { useCMS } from "../hooks/useCMS";

type VisaInfo = {
  title?: string;
  description?: string;
};

type VisaDestination = {
  id: string;
  title: string;
  slug: string;
  country?: string | null;
  description?: string;
  subDescription?: string;
  subtitle?: string;
  processingTime?: string;
  supportInfo?: string;
  highlights?: string[];
  overviewTitle?: string;
  overviewDescription?: string;
  quickSupportTitle?: string;
  quickSupportDescription?: string;
  supportIncluded?: string[];
  visaDetails?: VisaInfo[];
  requirements?: string[];
  heroImageUrl?: string;
  imageUrl?: string;
};

const normalizeCountry = (value?: string | null) => value?.trim().toLowerCase() || "";
const siteRegion = (import.meta.env.VITE_SITE_REGION || "IN").toUpperCase();
const siteCountry = siteRegion === "AE" ? "United Arab Emirates" : "India";
const isMatchingCountry = (country?: string | null) =>
  !normalizeCountry(country) || normalizeCountry(country) === normalizeCountry(siteCountry);

function VisaDestinationDetailPage() {
  const { slug } = useParams();
  const { data: destination, loading } = useCMS<VisaDestination>(() =>
    cmsApi.getVisaBySlug(slug!)
  );
  const [loadedHeroImage, setLoadedHeroImage] = useState<string | null>(null);
  const heroImage = destination?.heroImageUrl || destination?.imageUrl || null;

  useEffect(() => {
    if (!heroImage) {
      setLoadedHeroImage(null);
      return;
    }

    const image = new Image();
    image.onload = () => setLoadedHeroImage(heroImage);
    image.onerror = () => setLoadedHeroImage(destination?.imageUrl || null);
    image.src = heroImage;

    return () => {
      image.onload = null;
      image.onerror = null;
    };
  }, [heroImage, destination?.imageUrl]);

  if (loading) return <div className="py-20 text-center">Loading...</div>;
  if (!destination) return <Navigate to="/visa-services" replace />;
  if (!isMatchingCountry(destination.country)) return <Navigate to="/visa-services" replace />;

  const heroDescription = destination.subDescription || destination.description || "";
  const normalizedHeroDescription = heroDescription.trim().toLowerCase();

  const highlights =
    destination.highlights
      ?.map((item) => item?.trim())
      .filter((item): item is string => Boolean(item))
      .filter((item) => item.toLowerCase() !== normalizedHeroDescription) || [];

  const fallbackHeroHighlights = [
    destination.subtitle,
    destination.processingTime,
    destination.supportInfo,
  ]
    .map((item) => item?.trim())
    .filter((item): item is string => Boolean(item));

  const heroHighlights = (highlights.length > 0 ? highlights : fallbackHeroHighlights).slice(
    0,
    3
  );

  const overviewTitle = destination.overviewTitle || `About ${destination.title}`;
  const overviewDescription =
    destination.overviewDescription || destination.description || destination.subDescription || "";
  const overviewParagraphs = overviewDescription
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);

  const quickSupportTitle =
    destination.quickSupportTitle || "Responsive guidance before you apply";
  const quickSupportDescription =
    destination.quickSupportDescription ||
    "We help shortlist the likely checklist, align documents with the purpose of travel, and reduce confusion before submission.";

  const includedSupport = (
    destination.supportIncluded && destination.supportIncluded.length > 0
      ? destination.supportIncluded
      : [
          "Destination-specific document guidance",
          "Practical review support before submission",
          "Help choosing the next step for tourist travel plans",
        ]
  )
    .map((item) => item?.trim())
    .filter((item): item is string => Boolean(item));

  const facts =
    destination.visaDetails
      ?.map((item) => ({
        title: item?.title?.trim() || "",
        description: item?.description?.trim() || "",
      }))
      .filter((item) => item.title && item.description) || [];

  const requirements =
    destination.requirements
      ?.map((item) => item?.trim())
      .filter((item): item is string => Boolean(item)) || [];

  return (
    <div className="bg-white">
      <section className="relative overflow-hidden bg-brand-dark pb-20 pt-32 md:pb-28 md:pt-40">
        <div className="absolute inset-0">
          <div
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-700"
            style={{
              backgroundImage: loadedHeroImage ? `url(${loadedHeroImage})` : undefined,
              opacity: loadedHeroImage ? 0.78 : 0,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-dark/55 via-brand-dark/35 to-brand-dark/20" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.24),transparent_42%)]" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link
            to="/visa-services"
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-white transition-colors hover:bg-white/15"
          >
            <FaArrowLeft className="text-xs" />
            Back to Visa Services
          </Link>

          <div className="grid items-end gap-10 lg:grid-cols-[minmax(0,1.2fr)_360px]">
            <div>
              <span className="mb-4 inline-block rounded-full border border-brand-pink/30 bg-brand-pink/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-brand-pink">
                India Visa Destination
              </span>

              <h1 className="mb-5 font-heading text-4xl font-bold text-white md:text-6xl">
                {destination.title}
              </h1>

              <p className="max-w-3xl text-base leading-8 text-gray-200 md:text-lg">
                {heroDescription}
              </p>

              {heroHighlights.length > 0 && (
                <div className="mt-8 flex flex-wrap gap-3">
                  {heroHighlights.map((item, index) => (
                    <span
                      key={`${destination.slug}-hero-highlight-${index}`}
                      className="rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-white"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-white/10 p-6 text-white shadow-2xl backdrop-blur">
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-pink/20 text-brand-pink">
                <FaPassport className="text-2xl" />
              </div>

              <h2 className="mb-3 text-2xl font-semibold">Need help with your file?</h2>
              <p className="text-sm leading-7 text-gray-200">
                Share your travel plan with our team and we will help you understand
                the next steps, documents, and submission flow for this destination.
              </p>

              <div className="mt-6 space-y-3">
                <a
                  href={indiaSupport.whatsappHref}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-sm transition-colors hover:bg-white/15"
                >
                  <FaWhatsapp className="text-brand-pink" />
                  {indiaSupport.whatsappDisplay}
                </a>

                <a
                  href={indiaSupport.phoneHref}
                  className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-sm transition-colors hover:bg-white/15"
                >
                  <FaPhoneAlt className="text-brand-pink" />
                  {indiaSupport.phoneDisplay}
                </a>

                <a
                  href={indiaSupport.emailHref}
                  className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 text-sm transition-colors hover:bg-white/15"
                >
                  <FaEnvelope className="text-brand-pink" />
                  {indiaSupport.email}
                </a>
              </div>

              <a
                href={`/visa-services?destination=${destination.slug}#visa-application-form`}
                className="mt-6 inline-flex w-full items-center justify-center rounded-2xl bg-brand-pink px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-pink/90"
              >
                Start Application
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:px-8">
          <div>
            <span className="mb-3 inline-block text-sm font-semibold uppercase tracking-[0.25em] text-brand-pink">
              Overview
            </span>
            <h2 className="mb-5 font-heading text-3xl font-bold text-brand-dark md:text-4xl">
              {overviewTitle}
            </h2>

            <div className="space-y-5 text-base leading-8 text-gray-600">
              {overviewParagraphs.map((paragraph, index) => (
                <p key={`${destination.slug}-overview-${index}`}>{paragraph}</p>
              ))}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
            <div className="rounded-[2rem] border border-gray-100 bg-white p-6 shadow-sm">
              <span className="mb-2 inline-block text-xs font-semibold uppercase tracking-[0.25em] text-brand-pink">
                Quick Support
              </span>
              <h3 className="mb-3 text-2xl font-semibold text-brand-dark">
                {quickSupportTitle}
              </h3>
              <p className="text-sm leading-7 text-gray-600">
                {quickSupportDescription}
              </p>
            </div>

            <div className="rounded-[2rem] bg-gray-50 p-6">
              <span className="mb-2 inline-block text-xs font-semibold uppercase tracking-[0.25em] text-brand-pink">
                Included Support
              </span>
              <ul className="space-y-3 text-sm text-gray-600">
                {includedSupport.map((item, index) => (
                  <li key={`${destination.slug}-support-${index}`} className="flex items-start gap-3">
                    <FaCheckCircle className="mt-1 text-brand-pink" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {facts.length > 0 && (
        <section className="bg-gray-50 py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <span className="mb-3 inline-block text-sm font-semibold uppercase tracking-[0.25em] text-brand-pink">
              Visa Info
            </span>
            <h2 className="mb-10 font-heading text-3xl font-bold text-brand-dark md:text-4xl">
              {destination.title} Details
            </h2>

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {facts.map((fact, index) => (
                <div
                  key={`${destination.slug}-fact-${index}`}
                  className="rounded-[1.75rem] border border-white bg-white p-6 shadow-sm"
                >
                  <p className="text-sm font-medium uppercase tracking-[0.2em] text-gray-400">
                    {fact.title}
                  </p>
                  <p className="mt-3 text-2xl font-semibold text-brand-dark">
                    {fact.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {requirements.length > 0 && (
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <span className="mb-3 inline-block text-sm font-semibold uppercase tracking-[0.25em] text-brand-pink">
              Documents
            </span>
            <h2 className="mb-10 font-heading text-3xl font-bold text-brand-dark md:text-4xl">
              {destination.title} Requirements
            </h2>

            <div className="grid gap-4 md:grid-cols-2">
              {requirements.map((requirement, index) => (
                <div
                  key={`${destination.slug}-requirement-${index}`}
                  className="rounded-[1.75rem] border border-gray-100 bg-white p-6 shadow-sm"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-brand-pink/10 text-brand-pink">
                      <FaCheckCircle />
                    </div>
                    <p className="text-sm leading-7 text-gray-600">{requirement}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-[1.75rem] border border-brand-pink/10 bg-brand-pink/5 p-5 text-sm text-gray-700">
              <span className="font-semibold text-brand-dark">Note:</span> Interview appointment
              availability can affect the effective timeline beyond the core application
              preparation window.
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default VisaDestinationDetailPage;
